import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Center,
  Input,
  InputGroup,
  SimpleGrid,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { StoreProduct } from '@bofrak-backend/shared';
import {
  useStoreProducts,
  useAppSyncProductEventsWebsockets,
} from '@bofrak-backend/shared-ui';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  merchantAtom,
  selectedStoreAtom,
  storeProductsAtom,
  searchResultsAtom,
  accessTokenAtom,
} from '../recoil/atoms';
import debounce from 'lodash/debounce';
import { BASE_URL } from '../constants/environment';
import LiteProductCard from './LiteProductCard';
import BarcodeManagerModal from './barcode-labels/BarcodeManagerModal';

const ProductsTab: React.FC = () => {
  const currentStore = useRecoilValue(selectedStoreAtom);
  const merchant = useRecoilValue(merchantAtom);
  const products = useRecoilValue(storeProductsAtom);
  const filteredProducts = useRecoilValue(searchResultsAtom);
  const accessToken = useRecoilValue(accessTokenAtom);

  const setProducts = useSetRecoilState(storeProductsAtom);
  const setSearchResults = useSetRecoilState(searchResultsAtom);

  const [searchQuery, setSearchQuery] = useState('');
  const [barcodeProduct, setBarcodeProduct] = useState<StoreProduct | null>(null);

  const columns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 }) ?? 2;

  // Use IndexedDB-based store products
  const {
    products: hookProducts,
    isInitialLoad,
  } = useStoreProducts({
    storeId: currentStore?.id || '',
    merchantId: merchant?.id || '',
    includeDeleted: false,
    autoSync: true,
    apiEndpoint: BASE_URL,
    accessToken: accessToken ?? '',
  });

  // Sync hook products into Recoil atoms so the UI reads them
  useEffect(() => {
    setProducts(hookProducts);
    setSearchResults(hookProducts);
  }, [hookProducts, setProducts, setSearchResults]);

  // WebSocket for real-time updates
  useAppSyncProductEventsWebsockets({
    storeId: currentStore?.id || '',
    enabled: !!currentStore?.id,
  });

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  const handleOpenBarcode = useCallback((product: StoreProduct) => {
    setBarcodeProduct(product);
  }, []);

  const handleCloseBarcode = useCallback(() => {
    setBarcodeProduct(null);
  }, []);

  // Debounced search — runs 300ms after user stops typing
  const debouncedFilter = useMemo(
    () =>
      debounce((query: string, allProducts: StoreProduct[]) => {
        const filtered = query.trim()
          ? allProducts.filter((p) =>
              p.name?.toLowerCase().includes(query.toLowerCase()),
            )
          : allProducts;
        setSearchResults(filtered);
      }, 300),
    [setSearchResults],
  );

  useEffect(() => {
    debouncedFilter(searchQuery, products);
    return () => debouncedFilter.cancel();
  }, [searchQuery, products, debouncedFilter]);

  // Show cached products immediately from IndexedDB; sync happens in background
  const hasProducts = filteredProducts.length > 0;

  return (
    <Box>
      {/* Search Bar */}
      <Box mb={3} px={2}>
        <InputGroup>
          <Input
            placeholder={`Search ${currentStore?.name || 'products'}...`}
            value={searchQuery}
            onChange={handleSearch}
            borderRadius="full"
            borderColor="gray.300"
            _focus={{ borderColor: 'teal.400', boxShadow: 'none' }}
          />
        </InputGroup>
        {isInitialLoad && hasProducts && (
          <Text fontSize="xs" color="teal.500" mt={1} textAlign="center">
            Syncing latest products…
          </Text>
        )}
      </Box>

      {/* Product Grid — render immediately if cached IndexedDB data exists */}
      {hasProducts ? (
        <SimpleGrid columns={columns} spacing={3} px={2} pb={4}>
          {filteredProducts.map((product) => (
            <LiteProductCard
              key={product.id}
              product={product}
              onOpenBarcode={handleOpenBarcode}
            />
          ))}
        </SimpleGrid>
      ) : isInitialLoad ? (
        <Center py={20}>
          <VStack spacing={4}>
            <Text color="gray.500">Loading products…</Text>
          </VStack>
        </Center>
      ) : (
        <Center py={20}>
          <VStack spacing={4}>
            <Text color="gray.500" fontSize="lg">
              {searchQuery
                ? `No products match "${searchQuery}"`
                : 'No products found in this store'}
            </Text>
          </VStack>
        </Center>
      )}

      {/* Barcode Manager Modal */}
      {barcodeProduct && (
        <BarcodeManagerModal
          isOpen={!!barcodeProduct}
          onClose={handleCloseBarcode}
          product={barcodeProduct}
          onlyShowDefault={false}
        />
      )}
    </Box>
  );
};

export default ProductsTab;
